import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

const STATUS_VARIANT = { PROPOSED: 'warning', EXECUTED: 'success', CANCELLED: 'neutral' };

export function PaymentRunDetailModal({ runId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['finance', 'payment-run', runId], queryFn: () => financeApi.getPaymentRun(runId).then((r) => r.items), enabled: Boolean(runId) });
  const run = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['finance', 'payment-runs'] });
    queryClient.invalidateQueries({ queryKey: ['finance', 'payment-run', runId] });
    queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
  };
  const run_ = (fn) => ({ mutationFn: fn, onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')) });
  const execute = useMutation(run_(() => financeApi.executePaymentRun(runId)));
  const cancel = useMutation(run_(() => financeApi.cancelPaymentRun(runId)));
  const removeItem = useMutation(run_((itemId) => financeApi.removePaymentRunItem(runId, itemId)));
  const busy = execute.isPending || cancel.isPending;
  const proposed = run?.status === 'PROPOSED';

  return (
    <Modal
      open={Boolean(runId)}
      onClose={onClose}
      title={run ? `${run.runNumber}` : t('pr.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {proposed && <Button variant="ghost" disabled={busy} onClick={() => cancel.mutate()}>{t('pr.action.cancel')}</Button>}
          {proposed && <Button variant="primary" disabled={busy || (run.items || []).length === 0} onClick={() => execute.mutate()}>{t('pr.action.execute')}</Button>}
        </>
      }
    >
      {run && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={STATUS_VARIANT[run.status]}>{t(`pr.status.${run.status}`)}</Badge>
            {t('pr.col.paymentDate')}: {formatDate(run.paymentDate)} · {t('pr.field.dueBy')}: {formatDate(run.dueBy)}
            · <strong>{formatCurrency(run.totalAmount)}</strong> ({run.itemCount})
          </p>
          {run.status === 'EXECUTED' && <div className="alert success">{t('pr.detail.executed', { n: (run.items || []).filter((i) => i.paid).length })}</div>}

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('pr.item.invoice')}</th>
                  <th className="num">{t('pr.item.amount')}</th>
                  <th>{t('pr.item.state')}</th>
                  {proposed && <th />}
                </tr>
              </thead>
              <tbody>
                {(run.items || []).map((it) => (
                  <tr key={it.id}>
                    <td className="mono">{it.invoiceNumber}</td>
                    <td className="num mono">{formatCurrency(it.amount)}</td>
                    <td>
                      {run.status === 'EXECUTED'
                        ? (it.paid ? <Badge variant="success">{t('pr.item.paid')}</Badge> : <Badge variant="danger" title={it.error}>{t('pr.item.failed')}</Badge>)
                        : <span className="muted text-sm">{t('pr.item.pending')}</span>}
                    </td>
                    {proposed && <td className="num"><Button size="sm" variant="ghost" disabled={removeItem.isPending} onClick={() => removeItem.mutate(it.id)}>✕</Button></td>}
                  </tr>
                ))}
                {(run.items || []).length === 0 && <tr><td colSpan={4} className="muted">{t('pr.detail.noItems')}</td></tr>}
              </tbody>
            </table>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
